import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "./auth/AuthContext";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from "firebase/firestore";
import { db } from "./firebase";

function chipColor(tag) {
  const palette = [
    "#ffd166",
    "#8ecae6",
    "#b8c1ff",
    "#d4a373",
    "#9bf6ff",
    "#ffadad",
  ];
  const idx = Math.abs(tag.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0)) % palette.length;
  return palette[idx];
}

function TagList({ tags }) {
  if (!tags?.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
      {tags.map((tag) => (
        <span
          key={tag}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            borderRadius: 20,
            background: chipColor(tag.toLowerCase()),
            color: "#0f0f0f",
            fontWeight: 700,
            letterSpacing: 0.2,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

export default function FoodPairing() {
  const { user } = useAuth();
  const [pairings, setPairings] = useState([]);
  const [filterBase, setFilterBase] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [form, setForm] = useState({
    base: "",
    partner: "",
    why: "",
    notes: "",
    tags: "",
    synergy: 6,
  });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!user) return undefined;
    const q = query(
      collection(db, "foodPairings"),
      where("ownerId", "==", user.uid),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      const next = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setPairings(next);
    });
    return () => unsub();
  }, [user]);

  const allTags = useMemo(() => {
    const counts = {};
    pairings.forEach((p) => (p.tags || []).forEach((t) => {
      const key = t.toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    }));
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([tag]) => tag);
  }, [pairings]);

  const groupedByBase = useMemo(() => {
    const bucket = {};
    pairings.forEach((p) => {
      const key = p.base.trim();
      if (!bucket[key]) bucket[key] = { count: 0, favorites: 0, tags: {}, top: [] };
      bucket[key].count += 1;
      if (p.favorite) bucket[key].favorites += 1;
      (p.tags || []).forEach((t) => {
        const tagKey = t.toLowerCase();
        bucket[key].tags[tagKey] = (bucket[key].tags[tagKey] || 0) + 1;
      });
      bucket[key].top.push(p);
    });
    return bucket;
  }, [pairings]);

  const filteredPairings = useMemo(() => {
    return pairings.filter((p) => {
      const baseOk = !filterBase || p.base.toLowerCase().includes(filterBase.toLowerCase());
      const tagOk = !filterTag || (p.tags || []).some((t) => t.toLowerCase() === filterTag.toLowerCase());
      const favOk = !onlyFavorites || !!p.favorite;
      return baseOk && tagOk && favOk;
    });
  }, [pairings, filterBase, filterTag, onlyFavorites]);

  const resetForm = () => {
    setForm((prev) => ({
      ...prev,
      partner: "",
      why: "",
      notes: "",
      tags: "",
      synergy: 6,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.base.trim() || !form.partner.trim()) {
      setStatus("Add both an anchor ingredient and its pairing.");
      return;
    }
    setSaving(true);
    setStatus("Saving to Firestore…");
    try {
      const tags = form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      await addDoc(collection(db, "foodPairings"), {
        base: form.base.trim(),
        partner: form.partner.trim(),
        why: form.why.trim(),
        notes: form.notes.trim(),
        tags,
        synergy: Number(form.synergy) || 0,
        favorite: false,
        ownerId: user.uid,
        ownerEmail: user.email || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      resetForm();
      setStatus("Saved.");
    } catch (err) {
      console.error(err);
      setStatus(`Could not save: ${err.message || err.toString()}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleFavorite = async (p) => {
    await updateDoc(doc(db, "foodPairings", p.id), {
      favorite: !p.favorite,
      updatedAt: serverTimestamp(),
    });
  };

  const handleDelete = async (p) => {
    const sure = window.confirm(`Delete pairing ${p.base} + ${p.partner}?`);
    if (!sure) return;
    await deleteDoc(doc(db, "foodPairings", p.id));
  };

  const setFromBase = (base) => {
    setFilterBase(base);
    setForm((prev) => ({ ...prev, base }));
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "radial-gradient(circle at 20% 20%, #fef2e5, #f5f2ff 45%, #e7f7ff 90%)",
        padding: "32px 16px 64px",
        fontFamily: "system-ui, 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gap: 24 }}>
        <header style={{ display: "grid", gap: 10 }}>
          <div style={{ letterSpacing: 1.2, color: "#8c6239", fontWeight: 700 }}>Flavor Lab</div>
          <h1 style={{ margin: 0, fontSize: 36, letterSpacing: -0.5 }}>Food Pairing Database</h1>
          <p style={{ maxWidth: 740, lineHeight: 1.6, color: "#2f2f2f", margin: 0 }}>
            Log ingredient pairings, score their synergy, and keep tasting notes. Everything you save lands in Firestore,
            so you can build your own playbook of flavor matches inspired by the Foodpairing research.
          </p>
        </header>

        <section
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 16,
          }}
        >
          <div style={statCardStyle}>
            <div style={statLabel}>Pairings</div>
            <div style={statValue}>{pairings.length}</div>
            <div style={statHint}>Saved combinations</div>
          </div>
          <div style={statCardStyle}>
            <div style={statLabel}>Anchor ingredients</div>
            <div style={statValue}>{Object.keys(groupedByBase).length}</div>
            <div style={statHint}>Unique bases</div>
          </div>
          <div style={statCardStyle}>
            <div style={statLabel}>Flavor tags</div>
            <div style={statValue}>{allTags.length}</div>
            <div style={statHint}>Aromas / notes</div>
          </div>
        </section>

        <section style={panelStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 14, color: "#5f5f5f" }}>Filter</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                <input
                  value={filterBase}
                  onChange={(e) => setFilterBase(e.target.value)}
                  placeholder="Search base ingredient"
                  style={inputStyle}
                />
                <select
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  style={{ ...inputStyle, width: 180 }}
                >
                  <option value="">Any tag</option>
                  {allTags.map((tag) => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={onlyFavorites}
                    onChange={(e) => setOnlyFavorites(e.target.checked)}
                  />
                  Favorites only
                </label>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { setFilterBase(""); setFilterTag(""); setOnlyFavorites(false); }}
              style={ghostButton}
            >
              Clear filters
            </button>
          </div>
        </section>

        <section style={panelStyle}>
          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, letterSpacing: 0.4, color: "#7a4b27", fontWeight: 700 }}>Capture</div>
                <h3 style={{ margin: "4px 0 0" }}>Add a pairing to Firestore</h3>
              </div>
              <div style={{ color: "#7a7a7a", fontSize: 14 }}>{status}</div>
            </div>
            <form onSubmit={handleSave} style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                <div style={{ display: "grid", gap: 6 }}>
                  <label style={labelStyle}>Anchor ingredient</label>
                  <input
                    value={form.base}
                    onChange={(e) => setForm({ ...form, base: e.target.value })}
                    placeholder="e.g., Scallop"
                    style={inputStyle}
                  />
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  <label style={labelStyle}>Pairing ingredient</label>
                  <input
                    value={form.partner}
                    onChange={(e) => setForm({ ...form, partner: e.target.value })}
                    placeholder="e.g., Vanilla, yuzu, roast chicken fat"
                    style={inputStyle}
                  />
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  <label style={labelStyle}>Synergy score (0-10)</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={form.synergy}
                    onChange={(e) => setForm({ ...form, synergy: e.target.value })}
                    style={inputStyle}
                  />
                </div>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <label style={labelStyle}>Why it works</label>
                <textarea
                  value={form.why}
                  onChange={(e) => setForm({ ...form, why: e.target.value })}
                  placeholder="Share the aromatic bridge, texture contrast, or chef who inspired it."
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <label style={labelStyle}>Tags (comma-separated)</label>
                <input
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="citrus, umami, caramel, smoke"
                  style={inputStyle}
                />
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <label style={labelStyle}>Technique / notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="E.g., cure scallops, torch lightly; vanilla brown butter; serve chilled."
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <button type="button" onClick={resetForm} style={ghostButton}>Reset</button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    background: "linear-gradient(120deg, #ff8a00, #ff3d00)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    padding: "12px 18px",
                    fontWeight: 700,
                    letterSpacing: 0.2,
                    cursor: "pointer",
                    boxShadow: "0 10px 24px rgba(255,61,0,0.25)",
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? "Saving…" : "Save to Firestore"}
                </button>
              </div>
            </form>
          </div>
        </section>

        <section style={{ ...panelStyle, display: "grid", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, letterSpacing: 0.4, color: "#7a4b27", fontWeight: 700 }}>Atlas</div>
            <h3 style={{ margin: 0 }}>Anchor ingredients</h3>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            {Object.entries(groupedByBase).map(([base, info]) => {
              const topTags = Object.entries(info.tags)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([tag]) => tag);
              return (
                <button
                  key={base}
                  onClick={() => setFromBase(base)}
                  style={{
                    textAlign: "left",
                    border: "1px solid #e0d9d2",
                    background: "#fff",
                    borderRadius: 12,
                    padding: "14px 16px",
                    cursor: "pointer",
                    boxShadow: "0 6px 14px rgba(0,0,0,0.08)",
                    transition: "transform 120ms ease, box-shadow 120ms ease",
                  }}
                >
                  <div style={{ fontWeight: 800, letterSpacing: -0.1 }}>{base}</div>
                  <div style={{ fontSize: 13, color: "#5f5f5f", marginTop: 4 }}>
                    {info.count} pairings · {info.favorites} favorites
                  </div>
                  <TagList tags={topTags} />
                </button>
              );
            })}
            {pairings.length === 0 && (
              <div style={{ padding: 12, color: "#6a6a6a" }}>
                Start adding pairings to see your atlas.
              </div>
            )}
          </div>
        </section>

        <section style={{ ...panelStyle, display: "grid", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, letterSpacing: 0.4, color: "#7a4b27", fontWeight: 700 }}>Library</div>
            <h3 style={{ margin: 0 }}>Pairings</h3>
            <div style={{ color: "#6a6a6a" }}>{filteredPairings.length} shown</div>
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            {filteredPairings.map((p) => (
              <div
                key={p.id}
                style={{
                  borderRadius: 14,
                  border: "1px solid #e2dcd4",
                  background: "#fff",
                  padding: "16px 18px",
                  boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
                  display: "grid",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "grid", gap: 4 }}>
                    <div style={{ fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase", color: "#8c6239", fontWeight: 700 }}>Pairing</div>
                    <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.2 }}>
                      {p.base} <span style={{ color: "#9c6644" }}>+</span> {p.partner}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{
                      padding: "6px 10px",
                      borderRadius: 10,
                      background: "#0c7a58",
                      color: "#fff",
                      fontWeight: 700,
                      letterSpacing: 0.3,
                    }}>
                      {p.synergy ?? 0}/10
                    </div>
                    <button
                      onClick={() => toggleFavorite(p)}
                      style={{
                        ...ghostButton,
                        borderColor: p.favorite ? "#f5a524" : "#d7d7d7",
                        color: p.favorite ? "#c47300" : "#444",
                      }}
                    >
                      {p.favorite ? "★ Favorite" : "☆ Favorite"}
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      style={{ ...ghostButton, color: "#b3261e", borderColor: "#f1c7c3" }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {p.why && <div style={{ color: "#303030" }}>{p.why}</div>}
                {p.notes && <div style={{ color: "#505050", fontSize: 14 }}>Notes: {p.notes}</div>}
                <TagList tags={p.tags} />
              </div>
            ))}
            {filteredPairings.length === 0 && (
              <div style={{ color: "#6a6a6a" }}>
                No pairings match the current filters yet.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

const panelStyle = {
  background: "#fff",
  borderRadius: 16,
  border: "1px solid #e3dcd4",
  padding: "18px 18px 20px",
  boxShadow: "0 20px 40px rgba(0,0,0,0.06)",
};

const statCardStyle = {
  background: "#fff",
  borderRadius: 16,
  padding: "14px 16px 16px",
  border: "1px solid #e2dcd4",
  boxShadow: "0 16px 30px rgba(0,0,0,0.05)",
};

const statLabel = { fontSize: 12, color: "#6a6a6a", letterSpacing: 0.4, textTransform: "uppercase" };
const statValue = { fontSize: 28, fontWeight: 800, letterSpacing: -0.2, marginTop: 4 };
const statHint = { color: "#7a7a7a", fontSize: 14 };

const inputStyle = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: 10,
  border: "1px solid #d8d2ca",
  background: "#fff",
  fontSize: 15,
};

const labelStyle = { fontSize: 14, fontWeight: 700, color: "#3b322d" };

const ghostButton = {
  border: "1px solid #d7d7d7",
  background: "#fff",
  padding: "10px 14px",
  borderRadius: 10,
  cursor: "pointer",
  fontWeight: 700,
};
